import { all, delegate } from '../lib/dom';

const OPEN = 'is-open';
const ACTIVE = 'is-active';

function closeAll(): void {
  for (const menu of all('.sub-menu.' + OPEN)) {
    menu.classList.remove(OPEN);
    const trigger = menu.previousElementSibling;
    if (trigger instanceof HTMLElement) {
      trigger.classList.remove(ACTIVE);
      trigger.setAttribute('aria-expanded', 'false');
    }
  }
}

/**
 * Desktop dropdown navigation.
 *
 * Top-level links with children open their `.sub-menu`; anything else closes it.
 * The original marked the clicked link `is-active` but never cleared it, so links
 * accumulated the class for the life of the page — harmless (nothing styles it)
 * but wrong. Here it is cleared alongside the submenu it belongs to.
 */
/** A click on one of these is menu business, not an "outside" click. */
const INSIDE = '.site-navigation .menu > li > a, .sub-menu';

export function desktopMenu(): void {
  delegate(document, 'click', '.site-navigation .menu > li > a', (link, event) => {
    const submenu = link.parentElement?.querySelector<HTMLElement>(':scope > .sub-menu');

    // Placeholder parents ("#") exist only to open the dropdown.
    if (link.getAttribute('href') === '#' && link.closest('.menu-item-has-children')) {
      event.preventDefault();
    }

    if (!submenu) return;

    const wasOpen = submenu.classList.contains(OPEN);
    closeAll();

    if (!wasOpen) {
      submenu.classList.add(OPEN);
      link.classList.add(ACTIVE);
      link.setAttribute('aria-expanded', 'true');
    }
  });

  // Close on any click that is not the trigger and not inside the open dropdown.
  //
  // This deliberately does not lean on stopPropagation in the opener above: both
  // handlers live on `document`, and stopPropagation only stops the event moving
  // to the *next node*, not to the next listener on the same one. Guarding here
  // is what actually keeps the dropdown open — and it leaves unrelated document
  // click listeners (analytics, third-party pixels) alone.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(INSIDE)) {
      // The dropdown's ✕ is a ::before pseudo-element, so clicking it (or any
      // other bare spot on the panel) lands on the .sub-menu element itself.
      if (target.matches('.sub-menu')) closeAll();
      return;
    }
    closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });
}
